import { FC, useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react";
import {
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import { CloseIcon, DeleteIcon } from "../../../icons";
import { useClaimsStore } from "../../../../hooks/useClaimsStore";
import Row from "../../../ui/core/Row";
import {
  AllFilters,
  CLAIM_TYPE_OPTIONS,
  CLAIMS_COLUMN_FILTER_TYPE,
  CLAIMS_TABLE_COLUMN_OPTIONS,
  ColumnFilterEntry,
  FilterOperator,
  type ClaimsColumnFilterKind,
} from "../../../../types/claims";
import useNotifier from "../../../../hooks/useNotifier";
import type { OrderBy } from "../../../../stores/ClaimsStore";
import CoreInput from "../../../ui/core/CoreInput";
import StatusChip from "../table-components/StatusChip";

interface SaveViewModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const FIELD_LABELS: Record<string, string> = {
  status: "Status",
  claimType: "Claim type",
  payer: "Payer",
  provider: "Provider",
  denialReason: "Denial codes",
  assignedUser: "Assigned users",
  patient: "Patients",
  patientName: "Patient name",
  medicalRecordNumber: "MRN",
  dateOfService: "Date of service",
  dob: "Date of birth",
  placeOfService: "Place of service",
  patientBalance: "Patient balance",
  cptCode: "Procedure (CPT)",
  procedureCode: "Procedure (ICD-10-PCS)",
  createdAt: "Created at",
  updatedAt: "Updated at",
  claimNumber: "Claim number",
  patientId: "Patient ID",
  billedAmount: "Billed amount",
  paidAmount: "Paid amount",
  paidInclResubmission: "Paid incl. resubmission",
};

const OPERATOR_LABELS: Record<FilterOperator, string> = {
  and: "All (and)",
  or: "Any (or)",
  isnot: "Is not",
};

const getClaimsColumnFilterKind = (
  columnKey: string,
): ClaimsColumnFilterKind | undefined => {
  if (!(columnKey in CLAIMS_COLUMN_FILTER_TYPE)) {
    return undefined;
  }

  return CLAIMS_COLUMN_FILTER_TYPE[
    columnKey as keyof typeof CLAIMS_COLUMN_FILTER_TYPE
  ];
};

const getFilterOperatorLabel = (
  key: keyof AllFilters,
  raw: AllFilters[keyof AllFilters] | undefined,
): string => {
  const kind = getClaimsColumnFilterKind(String(key));
  if (
    raw &&
    Array.isArray(raw) &&
    (kind === "dateRange" || kind === "priceRange")
  ) {
    return "Between";
  }

  if (
    raw &&
    typeof raw === "object" &&
    !Array.isArray(raw) &&
    "operator" in raw
  ) {
    const op = (raw as ColumnFilterEntry).operator;
    return OPERATOR_LABELS[op] ?? String(op);
  }

  return "All (and)";
};

const getFilterValuesFromState = (
  filters: AllFilters,
  key: keyof AllFilters,
): string | React.ReactNode => {
  const raw = filters[key];

  if (!raw) {
    return "-";
  }

  if (Array.isArray(raw)) {
    return raw.filter((v) => v != null && String(v).trim() !== "").join(", ");
  }

  if (typeof raw === "object" && raw !== null && "values" in raw) {
    const values = (raw as ColumnFilterEntry).values as string[];

    if (key === "claimType") {
      return values
        .map(
          (v) =>
            CLAIM_TYPE_OPTIONS.find((o) => o.value === v)?.label ?? String(v),
        )
        .join(", ");
    }

    if (key === "status") {
      return (
        <Row sx={{ flexWrap: "wrap", gap: 1 }}>
          {values.map((status) => (
            <StatusChip status={status} />
          ))}
        </Row>
      );
    }

    return values.map((v) => String(v)).join(", ");
  }

  return "-";
};

const getOrderByFieldLabel = (field: string): string =>
  FIELD_LABELS[field] ?? field;

const formatOrderBySummary = (orderBy: OrderBy | null): string => {
  if (!orderBy) {
    return "Not set";
  }
  const fieldLabel = getOrderByFieldLabel(orderBy.field);
  const directionLabel =
    orderBy.direction === "asc" ? "Ascending" : "Descending";
  return `${fieldLabel} · ${directionLabel}`;
};

export const SaveViewModal: FC<SaveViewModalProps> = observer(
  ({ isOpen, onClose }) => {
    const notifier = useNotifier();
    const claimsStore = useClaimsStore();
    const allFilters = claimsStore.allFilters;
    const orderBy = claimsStore.orderBy;

    const visibleColumnLabels = useMemo(
      () =>
        CLAIMS_TABLE_COLUMN_OPTIONS.filter((col) =>
          claimsStore.isColumnVisible(col.id),
        ).map((col) => col.label),
      [claimsStore.columnSettings],
    );

    const hasNonDefaultColumnVisibility = useMemo(
      () =>
        CLAIMS_TABLE_COLUMN_OPTIONS.some(
          (col) =>
            claimsStore.isColumnVisible(col.id) !== (col.visible !== false),
        ),
      [claimsStore.columnSettings],
    );

    const selectedView = useMemo(
      () =>
        claimsStore.claimFilterViews.find(
          (view) => view.id === claimsStore.selectedFilterView,
        ) ?? null,
      [claimsStore.claimFilterViews, claimsStore.selectedFilterView],
    );

    const [viewName, setViewName] = useState("");
    const [localAllFilters, setLocalAllFilters] = useState<AllFilters | null>(
      allFilters,
    );
    const [isSaving, setIsSaving] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);

    useEffect(() => {
      if (isOpen) {
        setLocalAllFilters(allFilters);
        setViewName(selectedView?.name ?? "");
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allFilters, isOpen]);

    const handleRemoveFilter = (columnKey: string) => {
      setLocalAllFilters((prev) => {
        if (!prev) {
          return null;
        }

        const next = { ...prev } as Record<string, unknown>;
        delete next[columnKey];

        return Object.keys(next).length > 0 ? (next as AllFilters) : null;
      });
    };

    const handleClose = () => {
      onClose();
      setLocalAllFilters(null);
      setViewName("");
      setIsSaving(false);
      setIsUpdating(false);
    };

    const handleSaveView = async () => {
      setIsSaving(true);

      try {
        await claimsStore.createClaimFiltersView(
          viewName,
          localAllFilters as AllFilters,
        );
        notifier.showSuccess("View saved successfully");
        handleClose();
      } catch (error: any) {
        notifier.showError(error?.message || "Failed to save view");
        setIsSaving(false);
      }
    };

    const handleUpdateView = async () => {
      if (!selectedView) {
        return;
      }

      setIsUpdating(true);

      try {
        await claimsStore.updateClaimFiltersView(
          selectedView.id,
          viewName,
          localAllFilters as AllFilters,
        );
        notifier.showSuccess("View updated successfully");
        handleClose();
      } catch (error: any) {
        notifier.showError(error?.message || "Failed to update view");
        setIsUpdating(false);
      }
    };

    return (
      <Drawer
        anchor="right"
        open={isOpen}
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: 550,
            maxWidth: "100%",
          },
        }}
      >
        {/* Header */}
        <Row
          sx={{
            justifyContent: "space-between",
            alignItems: "center",
            padding: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
            height: "auto",
          }}
        >
          <Typography variant="h6" fontWeight={600}>
            Save current view
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Row>

        {/* Filter Content */}
        <Box
          sx={{
            flex: 1,
            overflow: "auto",
            padding: 2,
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <CoreInput
            label="View name"
            value={viewName}
            disabled={isSaving || isUpdating}
            onChange={(e) => setViewName(e.target.value)}
            fullWidth
            margin="normal"
          />

          <Typography variant="subtitle2" color="text.secondary">
            Filters applied to this view:
          </Typography>

          {orderBy?.field ? (
            <Box
              sx={{
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                p: 2,
                bgcolor: "action.hover",
              }}
            >
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ mb: 0.75 }}
              >
                Sort order
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {formatOrderBySummary(orderBy)}
              </Typography>
            </Box>
          ) : null}

          <Box
            sx={{
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              p: 2,
              bgcolor: "action.hover",
            }}
          >
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mb: 0.75 }}
            >
              Visible columns
            </Typography>
            <Typography variant="body2" fontWeight={500}>
              {visibleColumnLabels.join(" · ")}
            </Typography>
          </Box>

          {!localAllFilters ||
          Object.keys(localAllFilters).length === 0 ? null : (
            <>
              <TableContainer
                sx={{
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                  maxHeight: "100%",
                }}
              >
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, width: "30%" }}>
                        Field
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, width: "22%" }}>
                        Operator
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Value</TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontWeight: 600, width: 52, px: 1 }}
                      >
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {Object.entries(localAllFilters).map(([key]) => {
                      const filterKey = key as keyof AllFilters;
                      const raw = localAllFilters[filterKey];
                      const fieldOperator = getFilterOperatorLabel(
                        filterKey,
                        raw,
                      );
                      const fieldLabel = FIELD_LABELS[key] ?? String(key);
                      const fieldValue = getFilterValuesFromState(
                        localAllFilters,
                        key as keyof AllFilters,
                      );

                      return (
                        <TableRow key={key} hover>
                          <TableCell
                            component="th"
                            scope="row"
                            sx={{ fontWeight: 500 }}
                          >
                            {fieldLabel}
                          </TableCell>

                          <TableCell
                            sx={{
                              color: "text.secondary",
                            }}
                          >
                            {fieldOperator}
                          </TableCell>

                          <TableCell
                            sx={{
                              wordBreak: "break-word",
                              width: "100%",
                            }}
                          >
                            {fieldValue}
                          </TableCell>

                          <TableCell align="right" sx={{ width: 52, px: 0.5 }}>
                            <IconButton
                              size="small"
                              aria-label={`Remove ${key} filter`}
                              onClick={() => handleRemoveFilter(key)}
                              disabled={isSaving || isUpdating}
                            >
                              <DeleteIcon size={18} />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </Box>

        <Divider />

        {/* Footer Actions */}
        <Row
          sx={{
            padding: 2,
            gap: 2,
            justifyContent: "flex-end",
            height: "auto",
          }}
        >
          <Button
            variant="outlined"
            onClick={handleClose}
            sx={{ textTransform: "none" }}
            disabled={isSaving || isUpdating}
          >
            Cancel
          </Button>
          {selectedView ? (
            <Button
              variant="outlined"
              onClick={handleUpdateView}
              sx={{ textTransform: "none" }}
              disabled={
                isSaving ||
                isUpdating ||
                !viewName.trim() ||
                ((!localAllFilters ||
                  Object.keys(localAllFilters).length === 0) &&
                  !hasNonDefaultColumnVisibility)
              }
              loading={isUpdating}
            >
              Edit current view
            </Button>
          ) : null}
          <Button
            variant="contained"
            onClick={handleSaveView}
            sx={{ textTransform: "none" }}
            disabled={
              isSaving ||
              isUpdating ||
              !viewName.trim() ||
              ((!localAllFilters ||
                Object.keys(localAllFilters).length === 0) &&
                !hasNonDefaultColumnVisibility)
            }
            loading={isSaving}
          >
            Save View
          </Button>
        </Row>
      </Drawer>
    );
  },
);
